/**
 * SyncedPassage — EAA-1755.
 *
 * Presentational karaoke passage: renders text as word tokens and highlights the
 * active word. Pure UI (no audio logic) so it is reusable by any reading/speaking
 * surface. Pair it with `activeWordIndexAt` + an audio element to sync highlight
 * to playback, or drive `activeWordIndex` from any other source.
 *
 * In grading review, pass `wordScores` to colour each word by its pronunciation
 * bucket and show a per-word score + phone breakdown on hover.
 */

import * as React from 'react';
import { cn } from '../../lib/utils';
import {
    tokenizeReadingText,
    alignReadingTokens,
    type ReadingWordTimestamp,
} from '../../lib/reading-timing';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

/** One graded phoneme within a word (shown in the hover detail). */
export interface SyncedPhoneScore {
    phone: string;
    quality: number;
    soundLike?: string;
}

/** One graded word for per-word colour feedback + hover detail (EAA-1755). */
export interface SyncedWordScore {
    word: string;
    bucket: 'good' | 'ok' | 'bad';
    /** Overall 0-100 quality for the word (shown in the tooltip). */
    score?: number;
    /** Per-phone breakdown (shown in the tooltip). */
    phones?: SyncedPhoneScore[];
}

const SCORE_BUCKET_CLASS: Record<SyncedWordScore['bucket'], string> = {
    good: 'synced-passage-word--good text-success',
    ok: 'synced-passage-word--ok text-warning',
    bad: 'synced-passage-word--bad text-destructive underline decoration-destructive/50',
};

export interface SyncedPassageProps {
    /** The passage text to render. */
    text: string;
    /** Index of the word to highlight (-1 / undefined = none). */
    activeWordIndex?: number;
    /**
     * AI word timestamps. When supplied, tokens are aligned to the agent's
     * segmentation so highlight/seek stay correct even for words the synthesizer
     * split (e.g. "co-located"). Omit for browser-TTS mode (naive tokenisation).
     */
    wordTimestamps?: ReadingWordTimestamp[];
    /**
     * Per-word pronunciation scores (grading review). When supplied, each word is
     * coloured by its bucket and shows a score + phone detail on hover; tokens are
     * aligned to this list's segmentation (which can differ from the timestamps').
     */
    wordScores?: SyncedWordScore[];
    /** Tooltip heading for the per-word score (e.g. "Score"). */
    scoreLabel?: string;
    /** Register each word node so a parent can scroll it into view. */
    onWordRef?: (wordIndex: number, el: HTMLSpanElement | null) => void;
    /** When set, words become clickable (e.g. to seek playback to that word). */
    onWordClick?: (wordIndex: number) => void;
    className?: string;
    /** Extra class on each highlightable word span. */
    wordClassName?: string;
}

export const SyncedPassage: React.FC<SyncedPassageProps> = ({
    text,
    activeWordIndex = -1,
    wordTimestamps,
    wordScores,
    scoreLabel = 'Score',
    onWordRef,
    onWordClick,
    className,
    wordClassName,
}) => {
    const hasScores = Boolean(wordScores?.length);
    // EAA-1755: align tokens to the agent's word segmentation. In grading review
    // the score word list is authoritative (it can segment differently than the
    // timestamps — e.g. "co-located" is one word there, two in the timestamps).
    const tokens = React.useMemo(() => {
        if (wordScores?.length) {
            const src = wordScores.map((w) => ({ word: w.word, start: 0, end: 0 }));
            return alignReadingTokens(text, src);
        }
        if (wordTimestamps?.length) return alignReadingTokens(text, wordTimestamps);
        return tokenizeReadingText(text);
    }, [text, wordTimestamps, wordScores]);

    const renderTip = (ws: SyncedWordScore) => (
        <div className="synced-passage-tip space-y-1">
            {typeof ws.score === 'number' && (
                <div className="font-medium">
                    {scoreLabel}: {ws.score}/100
                </div>
            )}
            {ws.phones?.length ? (
                <div className="synced-passage-tip-phones space-y-0.5">
                    {ws.phones.map((p, pi) => (
                        <div
                            key={pi}
                            className="flex items-center justify-between gap-3 text-xs"
                        >
                            <span>
                                {p.phone}
                                {p.soundLike && p.soundLike !== p.phone ? ` → ${p.soundLike}` : ''}
                            </span>
                            <span className="tabular-nums text-muted-foreground">{p.quality}%</span>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );

    const body = tokens.map((tok, i) => {
        if (!tok.isWord) return <span key={i}>{tok.text}</span>;
        // A compound word spans a range; highlight while the active index is inside it.
        const isActive = activeWordIndex >= tok.wordIndex && activeWordIndex <= tok.wordEndIndex;
        const ws = hasScores ? wordScores?.[tok.wordIndex] : undefined;
        const scoreClass = ws ? SCORE_BUCKET_CLASS[ws.bucket] : undefined;

        const wordEl = (
            <span
                ref={onWordRef ? (el) => onWordRef(tok.wordIndex, el) : undefined}
                data-word-index={tok.wordIndex}
                role={onWordClick ? 'button' : undefined}
                tabIndex={onWordClick ? 0 : undefined}
                onClick={onWordClick ? () => onWordClick(tok.wordIndex) : undefined}
                onKeyDown={
                    onWordClick
                        ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onWordClick(tok.wordIndex);
                              }
                          }
                        : undefined
                }
                className={cn(
                    'synced-passage-word rounded-sm transition-colors duration-150',
                    onWordClick && 'cursor-pointer hover:bg-primary/10',
                    scoreClass,
                    isActive && 'synced-passage-word--active bg-primary/15 text-primary',
                    wordClassName
                )}
            >
                {tok.text}
            </span>
        );

        // Grading review: hovering a word reveals its score + phone detail.
        if (ws) {
            return (
                <Tooltip key={i}>
                    <TooltipTrigger asChild>{wordEl}</TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                        {renderTip(ws)}
                    </TooltipContent>
                </Tooltip>
            );
        }
        return React.cloneElement(wordEl, { key: i });
    });

    const passage = (
        <p
            data-slot="synced-passage"
            className={cn('synced-passage text-lg leading-relaxed text-foreground', className)}
        >
            {body}
        </p>
    );

    // A single provider wraps the passage so per-word tooltips work in review mode.
    return hasScores ? <TooltipProvider delayDuration={150}>{passage}</TooltipProvider> : passage;
};

export default SyncedPassage;
